import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  Activity, 
  Users, 
  AlertTriangle, 
  Shield, 
  Zap, 
  TrendingUp, 
  TrendingDown,
  Network,
  Target,
  Loader2,
  Play,
  Award,
  RefreshCw
} from "lucide-react";

interface NetworkSummaryProps {
  siteId?: string;
}

interface NetworkMetrics {
  healthScore: number;
  infectionVelocity: number;
  clusterRiskDistribution: { low: number; medium: number; high: number };
  wideBridgeCount: number;
  championCount: number;
  isolateCount: number;
  avgClusterSize: number;
  infectedClusters: number;
  totalClusters: number;
}

interface ActionRecommendation {
  id: string;
  type: 'vaccinate' | 'inject_champion' | 'quarantine' | 'strengthen_bridge';
  priority: 'critical' | 'high' | 'medium';
  title: string;
  description: string;
  targetId: string;
  targetName: string;
  impact: string;
}

export function NetworkSummary({ siteId }: NetworkSummaryProps) {
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<NetworkMetrics>({
    healthScore: 0,
    infectionVelocity: 0,
    clusterRiskDistribution: { low: 0, medium: 0, high: 0 },
    wideBridgeCount: 0,
    championCount: 0,
    isolateCount: 0,
    avgClusterSize: 0,
    infectedClusters: 0,
    totalClusters: 0
  });
  const [recommendations, setRecommendations] = useState<ActionRecommendation[]>([]);

  useEffect(() => {
    fetchNetworkMetrics();
  }, [siteId]);

  const fetchNetworkMetrics = async () => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from("worker_profiles")
        .select("company_id")
        .eq("user_id", session.user.id)
        .single();

      if (!profile?.company_id) return;

      // Fetch workers with network data
      let workersQuery = supabase
        .from("worker_profiles")
        .select("id, display_name, network_role, is_wide_bridge, current_risk_score, safety_state, cluster_id")
        .eq("company_id", profile.company_id);

      if (siteId) {
        workersQuery = workersQuery.eq("site_id", siteId);
      }

      const { data: workers } = await workersQuery;

      // Fetch clusters
      const { data: clusters } = await supabase
        .from("worker_clusters")
        .select("id, cluster_name, member_ids, is_infected, cluster_risk_score, infected_member_count")
        .eq("company_id", profile.company_id);

      // Calculate metrics
      const totalWorkers = workers?.length || 0;
      const wideBridges = workers?.filter(w => w.is_wide_bridge) || [];
      const champions = workers?.filter(w => w.network_role === 'champion') || [];
      const isolates = workers?.filter(w => w.network_role === 'isolate') || [];
      const highRiskWorkers = workers?.filter(w => (w.current_risk_score || 0) >= 60) || [];
      
      const infectedClusters = clusters?.filter(c => c.is_infected) || [];
      const totalClusters = clusters?.length || 0;
      const avgClusterSize = totalClusters > 0 
        ? (clusters?.reduce((sum, c) => sum + ((c.member_ids as string[])?.length || 0), 0) || 0) / totalClusters 
        : 0;

      // Calculate cluster risk distribution using cluster_risk_score
      const lowRiskClusters = clusters?.filter(c => (c.cluster_risk_score || 0) < 40).length || 0;
      const mediumRiskClusters = clusters?.filter(c => (c.cluster_risk_score || 0) >= 40 && (c.cluster_risk_score || 0) < 70).length || 0;
      const highRiskClusters = clusters?.filter(c => (c.cluster_risk_score || 0) >= 70).length || 0;

      // Calculate health score (0-100)
      const infectionRate = totalClusters > 0 ? infectedClusters.length / totalClusters : 0;
      const highRiskRate = totalWorkers > 0 ? highRiskWorkers.length / totalWorkers : 0;
      const championRate = totalWorkers > 0 ? champions.length / totalWorkers : 0;
      const healthScore = Math.round(
        (1 - infectionRate) * 40 + 
        (1 - highRiskRate) * 40 + 
        Math.min(championRate * 10, 1) * 20
      );

      // Calculate infection velocity (new infections per week - mock for now)
      const infectionVelocity = infectedClusters.length > 0 ? Math.round(infectedClusters.length * 0.3 * 10) / 10 : 0;

      setMetrics({
        healthScore,
        infectionVelocity,
        clusterRiskDistribution: { low: lowRiskClusters, medium: mediumRiskClusters, high: highRiskClusters },
        wideBridgeCount: wideBridges.length,
        championCount: champions.length,
        isolateCount: isolates.length,
        avgClusterSize: Math.round(avgClusterSize * 10) / 10,
        infectedClusters: infectedClusters.length,
        totalClusters
      });

      // Generate action recommendations
      const newRecommendations: ActionRecommendation[] = [];

      // 1. Vaccinate infected clusters
      for (const cluster of infectedClusters.slice(0, 2)) {
        const memberCount = (cluster.member_ids as string[])?.length || 0;
        newRecommendations.push({
          id: `vaccinate-${cluster.id}`,
          type: 'vaccinate',
          priority: 'critical',
          title: `Vaccinate Cluster: ${cluster.cluster_name || 'Unnamed'}`,
          description: `Deploy micro-learning to all ${memberCount} members to stop infection spread`,
          targetId: cluster.id,
          targetName: cluster.cluster_name || 'Cluster',
          impact: `Reduce cluster risk by ~30%`
        });
      }

      // 2. Strengthen wide bridges at risk
      for (const bridge of wideBridges.filter(w => (w.current_risk_score || 0) >= 50).slice(0, 2)) {
        newRecommendations.push({
          id: `strengthen-${bridge.id}`,
          type: 'strengthen_bridge',
          priority: 'high',
          title: `Protect Wide Bridge: ${bridge.display_name}`,
          description: `This bridge connects multiple clusters - their risk affects network health`,
          targetId: bridge.id,
          targetName: bridge.display_name || 'Worker',
          impact: `Prevent cross-cluster infection`
        });
      }

      // 3. Inject champion into high-risk clusters
      const highRiskClustersData = clusters?.filter(c => (c.cluster_risk_score || 0) >= 60 && !c.is_infected).slice(0, 2) || [];
      for (const cluster of highRiskClustersData) {
        newRecommendations.push({
          id: `inject-${cluster.id}`,
          type: 'inject_champion',
          priority: 'high',
          title: `Inject Champion into: ${cluster.cluster_name || 'Cluster'}`,
          description: `Assign or promote a safety champion to influence this high-risk cluster`,
          targetId: cluster.id,
          targetName: cluster.cluster_name || 'Cluster',
          impact: `Improve cluster safety culture`
        });
      }

      // 4. Quarantine chronic workers
      const chronicWorkers = workers?.filter(w => w.safety_state === 'chronic').slice(0, 2) || [];
      for (const worker of chronicWorkers) {
        newRecommendations.push({
          id: `quarantine-${worker.id}`,
          type: 'quarantine',
          priority: 'medium',
          title: `Supervisor Intervention: ${worker.display_name}`,
          description: `Chronic safety state requires supervisor commitment session`,
          targetId: worker.id,
          targetName: worker.display_name || 'Worker',
          impact: `Break negative behavior pattern`
        });
      }

      setRecommendations(newRecommendations);

    } catch (error) {
      console.error("Error fetching network metrics:", error);
      toast.error("Failed to load network metrics");
    } finally {
      setLoading(false);
    }
  };

  const executeAction = async (recommendation: ActionRecommendation) => {
    setExecuting(recommendation.id);
    try {
      switch (recommendation.type) {
        case 'vaccinate':
          // Trigger micro-learning for all cluster members
          await supabase.functions.invoke("trigger-automatic-interventions", {
            body: {
              clusterId: recommendation.targetId,
              triggerEvent: 'cluster_vaccination'
            }
          });
          toast.success(`Micro-learning deployed to ${recommendation.targetName}`);
          break;

        case 'strengthen_bridge':
        case 'quarantine':
          // Create supervisor task
          await supabase.from("supervisor_tasks").insert({
            title: recommendation.title,
            description: recommendation.description,
            source_type: 'network_analysis',
            source_id: recommendation.targetId,
            priority: recommendation.priority,
            status: 'pending',
            verification_required: true
          });
          toast.success("Supervisor task created");
          break;

        case 'inject_champion':
          // Generate intervention recommendation for champion assignment
          await supabase.functions.invoke("generate-intervention-recommendations", {
            body: {
              clusterId: recommendation.targetId,
              generateForCluster: true
            }
          });
          toast.success("Champion intervention generated");
          break;
      }

      // Remove executed recommendation
      setRecommendations(prev => prev.filter(r => r.id !== recommendation.id));

    } catch (error) {
      console.error("Error executing action:", error);
      toast.error("Failed to execute action");
    } finally {
      setExecuting(null);
    }
  };

  const getHealthColor = (score: number) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return "bg-destructive text-destructive-foreground";
      case 'high': return "bg-orange-500 text-white";
      case 'medium': return "bg-yellow-500 text-black";
      default: return "bg-muted";
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'vaccinate': return <Shield className="h-4 w-4" />;
      case 'inject_champion': return <Award className="h-4 w-4" />;
      case 'quarantine': return <AlertTriangle className="h-4 w-4" />;
      case 'strengthen_bridge': return <Network className="h-4 w-4" />;
      default: return <Zap className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Network Health Summary</h3>
          <p className="text-sm text-muted-foreground">
            Actionable insights based on Centola's Complex Contagion theory
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchNetworkMetrics}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Health Score Card */}
      <Card className={`border-2 ${metrics.healthScore >= 70 ? 'border-green-500/30' : metrics.healthScore >= 40 ? 'border-yellow-500/30' : 'border-red-500/30'}`}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-3 rounded-full ${metrics.healthScore >= 70 ? 'bg-green-500/10' : metrics.healthScore >= 40 ? 'bg-yellow-500/10' : 'bg-red-500/10'}`}>
                <Activity className={`h-6 w-6 ${getHealthColor(metrics.healthScore)}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Network Health Score</p>
                <p className={`text-3xl font-bold ${getHealthColor(metrics.healthScore)}`}>{metrics.healthScore}%</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-sm">
                {metrics.infectionVelocity > 0 ? (
                  <>
                    <TrendingDown className="h-4 w-4 text-destructive" />
                    <span className="text-destructive">{metrics.infectionVelocity} infections/week</span>
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="text-green-500">Stable</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <Progress value={metrics.healthScore} className="h-2" />
        </CardContent>
      </Card>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              Clusters
            </div>
            <div className="mt-1">
              <span className="text-2xl font-bold">{metrics.totalClusters}</span>
              {metrics.infectedClusters > 0 && (
                <span className="text-sm text-destructive ml-2">
                  ({metrics.infectedClusters} infected)
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Network className="h-4 w-4" />
              Wide Bridges
            </div>
            <div className="text-2xl font-bold mt-1">{metrics.wideBridgeCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Award className="h-4 w-4 text-green-500" />
              Champions
            </div>
            <div className="text-2xl font-bold mt-1 text-green-600">{metrics.championCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Target className="h-4 w-4" />
              Avg Cluster Size
            </div>
            <div className="text-2xl font-bold mt-1">{metrics.avgClusterSize}</div>
          </CardContent>
        </Card>
      </div>

      {/* Cluster Risk Distribution */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Cluster Risk Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-green-600">Low Risk</span>
                <span className="font-medium">{metrics.clusterRiskDistribution.low}</span>
              </div>
              <Progress value={metrics.totalClusters > 0 ? (metrics.clusterRiskDistribution.low / metrics.totalClusters) * 100 : 0} className="h-2 bg-muted [&>div]:bg-green-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-yellow-600">Medium Risk</span>
                <span className="font-medium">{metrics.clusterRiskDistribution.medium}</span>
              </div>
              <Progress value={metrics.totalClusters > 0 ? (metrics.clusterRiskDistribution.medium / metrics.totalClusters) * 100 : 0} className="h-2 bg-muted [&>div]:bg-yellow-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-red-600">High Risk</span>
                <span className="font-medium">{metrics.clusterRiskDistribution.high}</span>
              </div>
              <Progress value={metrics.totalClusters > 0 ? (metrics.clusterRiskDistribution.high / metrics.totalClusters) * 100 : 0} className="h-2 bg-muted [&>div]:bg-red-500" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Recommendations */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Recommended Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recommendations.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No urgent actions needed. Network is healthy!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recommendations.map((rec) => (
                <div
                  key={rec.id}
                  className="flex items-start justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-background">
                      {getActionIcon(rec.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-sm">{rec.title}</h4>
                        <Badge className={getPriorityColor(rec.priority)} variant="secondary">
                          {rec.priority}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{rec.description}</p>
                      <p className="text-xs text-primary mt-1">Impact: {rec.impact}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => executeAction(rec)}
                    disabled={executing === rec.id}
                  >
                    {executing === rec.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-1" />
                        Execute
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
